import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL query for getting ATM terminals
export const GET_ATM_TERMINALS = gql`
  query GetAtmTerminals {
    atmTerminals {
      id
      pinCode
      phoneForOtp
      port
      recyclerLimitBalance
      address
      remoteId
      userId
      balanceRecycler
      balanceCashbox
      createdAt
      updatedAt
      username
      shopBalance {
        amount
        credit
        onHold
        currency
      }
      remote {
        agentId
        deviceId
        machineName
        online
        ip
        city
        region
        country
        os
        osUser
        agentVersion
        lastSeenAt
        powerSupply
        disabled
        sessionsTotal
        canConnect
        permanentUrl
        hardware {
          cpu
          cpuCores
          cpuSpeedMhz
          ramTotalMb
          ramAvailableMb
          osName
          osVersion
          computerModel
          localIps
          macAddress
          disks {
            name
            fileSystem
            sizeMb
            freeMb
          }
        }
      }
      credentials {
        id
        atmProviderId
        agentName
        createdAt
        updatedAt
      }
    }
  }
`;

// Issues a single-use Getscreen remote-control link for a terminal
export const ATM_REMOTE_CONNECT_URL = gql`
  mutation AtmRemoteConnectUrl($terminalId: Int!) {
    atmRemoteConnectUrl(terminalId: $terminalId)
  }
`;

// GraphQL query for getting ATM providers
export const GET_ATM_PROVIDERS = gql`
  query AtmProviders {
    atmProviders {
      id
      name
      isActive
      iconUrl
      environmentId
      createdAt
      updatedAt
    }
  }
`;

// GraphQL mutation for creating ATM terminal
export const CREATE_ATM_TERMINAL = gql`
  mutation CreateAtmTerminal($input: CreateAtmTerminalInput!) {
    createAtmTerminal(input: $input) {
      id
      pinCode
      phoneForOtp
      port
      recyclerLimitBalance
      address
      remoteId
      userId
      balanceRecycler
      balanceCashbox
      createdAt
      updatedAt
      credentials {
            id
            atmProviderId
            agentName
            createdAt
            updatedAt
        }
    }
  }
`;

// GraphQL mutation for updating ATM terminal
export const UPDATE_ATM_TERMINAL = gql`
  mutation UpdateAtmTerminal($input: UpdateAtmTerminalInput!) {
    updateAtmTerminal(input: $input) {
      id
      pinCode
      phoneForOtp
      port
      recyclerLimitBalance
      address
      remoteId
      userId
      balanceRecycler
      balanceCashbox
      credentials {
        id
        atmProviderId
        agentName
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

// Balance object with bills breakdown
export interface AtmBalance {
  bills: Record<string, number>;
  total: number;
}

// Wallet balance of the shop user that owns the terminal
export interface AtmShopBalance {
  amount: string;
  credit: string;
  onHold: string;
  currency: string;
}

export interface AtmRemoteDisk {
  name: string | null;
  fileSystem: string | null;
  sizeMb: number | null;
  freeMb: number | null;
}

export interface AtmRemoteHardware {
  cpu: string | null;
  cpuCores: number | null;
  cpuSpeedMhz: number | null;
  ramTotalMb: number | null;
  ramAvailableMb: number | null;
  osName: string | null;
  osVersion: string | null;
  computerModel: string | null;
  localIps: string[] | null;
  macAddress: string | null;
  disks: AtmRemoteDisk[] | null;
}

// Live state of the ATM machine, from Getscreen.me
export interface AtmRemoteStatus {
  agentId: number;
  deviceId: number | null;
  machineName: string | null;
  online: boolean;
  ip: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  os: string | null;
  osUser: string | null;
  agentVersion: string | null;
  lastSeenAt: string | null;
  powerSupply: boolean | null;
  disabled: boolean | null;
  sessionsTotal: number | null;
  canConnect: boolean;
  permanentUrl: string | null;
  hardware: AtmRemoteHardware | null;
}

// Balance input for update
export interface AtmBalanceInput {
  amount: number;
  noteCount: number;
}

// Input types for creating ATM terminal
export interface AtmCredentialInput {
  atmProviderId: number;
  agentName: string;
  agentPassword: string;
  agentKey?: string;
}

export interface CreateAtmTerminalInput {
  pinCode: number;
  phoneForOtp: string;
  port: string;
  recyclerLimitBalance: string;
  address: string;
  remoteId: string;
  userId: number;
  balanceRecycler?: AtmBalance | null;
  balanceCashbox?: AtmBalance | null;
  credentials?: AtmCredentialInput[];
}

export interface UpdateAtmTerminalInput {
  id: number;
  pinCode?: number;
  phoneForOtp?: string;
  port?: string;
  recyclerLimitBalance?: string;
  address?: string;
  remoteId?: string;
  userId?: number;
  balanceRecycler?: AtmBalanceInput | null;
  balanceCashbox?: AtmBalanceInput | null;
  credentials?: AtmCredentialInput[];
}

// ATM Provider interface
export interface AtmProvider {
  id: number;
  name: string;
  isActive: boolean;
  iconUrl: string | null;
  environmentId: number;
  createdAt: string;
  updatedAt: string;
}

// Credential response type
export interface AtmCredential {
  id: number;
  atmProviderId: number;
  agentName: string;
  agentKey?: string;
  createdAt: string;
  updatedAt: string;
}

// Raw GraphQL response interface (before transformation)
export interface RawAtmTerminal {
  id: string;
  pinCode: number;
  phoneForOtp: string;
  port: string;
  recyclerLimitBalance: string;
  address: string;
  remoteId: string;
  userId: string;
  balanceRecycler: AtmBalance;
  balanceCashbox: AtmBalance;
  createdAt: string;
  updatedAt: string;
  username: string;
  shopBalance?: AtmShopBalance | null;
  remote?: AtmRemoteStatus | null;
}

// Transformed ATM Terminal interface (after conversion)
export interface AtmTerminal {
  id: number;
  pinCode: number;
  phoneForOtp: string;
  port: string;
  recyclerLimitBalance: string;
  address: string;
  remoteId: string;
  userId: number;
  balanceRecycler: AtmBalance;
  balanceCashbox: AtmBalance;
  createdAt: string;
  updatedAt: string;
  username?: string;
  shopBalance?: AtmShopBalance | null;
  remote?: AtmRemoteStatus | null;
}

// Created ATM terminal response
export interface CreatedAtmTerminal extends AtmTerminal {
  credentials: AtmCredential[];
}

export interface GetAtmTerminalsResponse {
  atmTerminals: RawAtmTerminal[];
}

export interface GetAtmProvidersResponse {
  atmProviders: AtmProvider[];
}

// AtmTerminals service class
export class AtmTerminalsService {
  private static instance: AtmTerminalsService;

  private constructor() { }

  public static getInstance(): AtmTerminalsService {
    if (!AtmTerminalsService.instance) {
      AtmTerminalsService.instance = new AtmTerminalsService();
    }
    return AtmTerminalsService.instance;
  }

  // Create ATM terminal
  async createAtmTerminal(input: CreateAtmTerminalInput): Promise<AtmTerminal> {
    try {
      const response = await apolloClient.mutate({
        mutation: CREATE_ATM_TERMINAL,
        variables: { input },
      });

      if (response.errors && response.errors.length > 0) {
        throw response;
      }

      if (!response.data || !response.data.createAtmTerminal) {
        throw new Error('No data returned from createAtmTerminal mutation');
      }

      const terminal = response.data.createAtmTerminal;
      return {
        ...terminal,
        id: Number(terminal.id),
        userId: Number(terminal.userId),
      } as AtmTerminal;
    } catch (error: any) {
      console.error('Error creating ATM terminal:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Get all ATM terminals
  async getAtmTerminals(): Promise<AtmTerminal[]> {
    try {
      const { data } = await apolloClient.query<GetAtmTerminalsResponse>({
        query: GET_ATM_TERMINALS,
        fetchPolicy: 'network-only',
      });

      if (!data?.atmTerminals) {
        throw new Error('No data returned from ATM terminals query');
      }

      // Transform the response to convert string IDs to numbers
      const transformedTerminals = data.atmTerminals.map((terminal) => ({
        ...terminal,
        id: Number(terminal.id),
        userId: Number(terminal.userId),
      }));

      return transformedTerminals;
    } catch (error: any) {
      console.error('Error fetching ATM terminals:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Update ATM terminal
  async updateAtmTerminal(input: UpdateAtmTerminalInput): Promise<AtmTerminal> {
    try {
      const response = await apolloClient.mutate({
        mutation: UPDATE_ATM_TERMINAL,
        variables: { input },
      });

      if (response.errors && response.errors.length > 0) {
        throw response;
      }

      if (!response.data || !response.data.updateAtmTerminal) {
        throw new Error('No data returned from updateAtmTerminal mutation');
      }

      const terminal = response.data.updateAtmTerminal;
      return {
        ...terminal,
        id: Number(terminal.id),
        userId: Number(terminal.userId),
      } as AtmTerminal;
    } catch (error: any) {
      console.error('Error updating ATM terminal:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Ask the backend for a single-use Getscreen remote-control link
  async getRemoteConnectUrl(terminalId: number): Promise<string> {
    const response = await apolloClient.mutate<{
      atmRemoteConnectUrl: string;
    }>({
      mutation: ATM_REMOTE_CONNECT_URL,
      variables: { terminalId },
    });

    if (!response.data?.atmRemoteConnectUrl) {
      throw new Error('No connect URL returned for this terminal');
    }

    return response.data.atmRemoteConnectUrl;
  }

  // Get all ATM providers
  async getAtmProviders(): Promise<AtmProvider[]> {
    try {
      const { data } = await apolloClient.query<GetAtmProvidersResponse>({
        query: GET_ATM_PROVIDERS,
        fetchPolicy: 'network-only',
      });

      if (!data?.atmProviders) {
        throw new Error('No data returned from ATM providers query');
      }

      return data.atmProviders;
    } catch (error: any) {
      console.error('Error fetching ATM providers:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const atmTerminalsService = AtmTerminalsService.getInstance();

// Export convenience functions
export async function getAtmTerminals(): Promise<AtmTerminal[]> {
  return atmTerminalsService.getAtmTerminals();
}

export async function createAtmTerminal(input: CreateAtmTerminalInput): Promise<AtmTerminal> {
  return atmTerminalsService.createAtmTerminal(input);
}

export async function getAtmProviders(): Promise<AtmProvider[]> {
  return atmTerminalsService.getAtmProviders();
}

export async function updateAtmTerminal(input: UpdateAtmTerminalInput): Promise<AtmTerminal> {
  return atmTerminalsService.updateAtmTerminal(input);
}

export async function getRemoteConnectUrl(terminalId: number): Promise<string> {
  return atmTerminalsService.getRemoteConnectUrl(terminalId);
}


